import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import {
  LibraryDocument,
  DocumentData,
  ProcessedSection,
  TemplateDocument,
} from "../types/document-library";
import { validateAdminAccess } from './adminService';

// Professional Default Template - Simplified with only essential variables
export const DEFAULT_TEMPLATES = [
  {
    id: "default-1",
    name: "Professional Business Proposal",
    description:
      "Clean, professional template with essential sections and minimal variables",
    isDefault: true,
    category: "Business",
    content: `# Professional Business Proposal

## Executive Summary
**Client:** [Client Name]
**Project:** [Project Title]
**Contact:** [Client Email]

This proposal outlines our recommended approach for your project based on your specific requirements and business objectives.

### Key Benefits
- Tailored solution designed for your specific needs
- Proven methodology with measurable outcomes
- Expert team with relevant experience
- Clear deliverables and professional execution

## Project Overview & Requirements Analysis
### Understanding Your Needs
Based on our analysis of your requirements, we understand that you need a comprehensive solution that addresses your specific business challenges and objectives for your project.

### Project Objectives
Our primary objectives for this project include delivering a solution that meets your stated requirements while providing measurable value to your organization.

## Proposed Solution & Approach
### Our Recommended Solution
We propose a tailored approach that leverages industry best practices and proven methodologies to deliver the results you need for your project.

### Implementation Methodology
Our implementation follows a structured approach with clear phases, milestones, and deliverables to ensure project success.

## Detailed Scope of Work
### Included Services
- Comprehensive project planning and management
- Solution design and development
- Testing and quality assurance
- Documentation and training
- Post-implementation support

### Deliverables
All deliverables will be clearly defined with acceptance criteria and delivery timelines based on the specific requirements of your project.

## Project Timeline & Milestones
We will provide a detailed project timeline with key milestones and deliverables upon project approval. The timeline will be customized based on the scope and complexity of your project.

## Team & Expertise
### Proposed Project Team
Our team consists of experienced professionals with relevant expertise for your project requirements.

### Relevant Experience
We have successfully delivered similar projects, providing us with valuable insights and proven approaches that will benefit your project.

## Investment & Budget Analysis
### Investment Overview
Our pricing is based on the project scope, complexity, and your specific requirements. We provide transparent pricing with clear value propositions.

### Value Proposition
This investment will deliver measurable returns through improved efficiency, reduced costs, and enhanced capabilities.

## Next Steps
1. Review and approve this proposal
2. Finalize project scope and timeline
3. Execute project agreement
4. Begin project kickoff process

---

**Proposal Validity:** 30 days from submission
**Contact Information:** [Client Email]
**Next Step:** Schedule a meeting to discuss project details

*We look forward to partnering with you on your project.*`,
    sections: [
      "Executive Summary",
      "Project Overview & Requirements Analysis",
      "Proposed Solution & Approach",
      "Detailed Scope of Work",
      "Project Timeline & Milestones",
      "Team & Expertise",
      "Investment & Budget Analysis",
      "Next Steps",
    ],
  },
];

// Get all templates (default + custom) - Hide defaults if user has custom versions
export const getAllTemplates = async (
  userId?: string
): Promise<LibraryDocument[]> => {
  if (!userId) {
    console.log(
      `✅ Total templates: ${DEFAULT_TEMPLATES.length} default only (no userId provided)`
    );
    return getDefaultTemplates();
  }

  const customTemplates = await fetchProposalTemplates(userId);

  // Always show both default and custom templates
  const defaultTemplates = getDefaultTemplates();
  console.log(
    `✅ Total templates: ${defaultTemplates.length} default + ${
      customTemplates.length
    } custom = ${defaultTemplates.length + customTemplates.length}`
  );
  return [...defaultTemplates, ...customTemplates];
};

// Get default templates (non-editable)
export const getDefaultTemplates = (): LibraryDocument[] => {
  return DEFAULT_TEMPLATES.map((template) => ({
    id: template.id,
    title: template.name,
    client: "System Template",
    date: new Date().toISOString(),
    type: template.category,
    sections: template.sections || [],
    rawData: {
      content: template.content,
      sections: [],
      metadata: {
        isDefault: template.isDefault,
        description: template.description,
      },
    },
  }));
};

// Parse sections from markdown content
const parseMarkdownSections = (content: string): string[] => {
  if (!content) return [];

  const lines = content.split("\n");
  const sections = [];

  for (const line of lines) {
    // Check for headers (# ## ### etc.)
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1]?.length || 0;
      const title = headerMatch[2]?.trim() || "";

      // Only consider level 1 and 2 headers as main sections
      if (level <= 2 && title) {
        sections.push(title);
      }
    }
  }

  return sections.length > 0 ? sections : ["Template Content"];
};

// Fetch custom templates from database
export const fetchProposalTemplates = async (
  userId: string
): Promise<LibraryDocument[]> => {
  try {
    console.log("🔍 Fetching proposal templates for user:", userId);

    const { data, error } = await supabase
      .from("scopingai_proposaltemplates")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("❌ Error fetching proposal templates:", error);
      return [];
    }

    console.log(
      `✅ Found ${data?.length || 0} proposal templates for user ${userId}`
    );

    // Process templates and fetch content from bucket if available
    const processedTemplates = await Promise.all(
      (data || []).map(async (template) => {
        let content = template.content || '';

        // If template has bucket_path, fetch content from storage
        if (template.bucket_path) {
          try {
            console.log(`📁 Fetching content from bucket: ${template.bucket_path}`);

            const { data: fileData, error: downloadError } = await supabase.storage
              .from('proposaltemplates')
              .download(template.bucket_path);

            if (downloadError) {
              console.error(`❌ Error downloading from bucket: ${downloadError.message}`);
              // Fallback to database content if available
              content = template.content || '';
            } else {
              // Convert blob to text
              content = await fileData.text();
              console.log(`✅ Content loaded from bucket: ${content.length} characters`);
            }
          } catch (bucketError) {
            console.error(`❌ Error processing bucket content:`, bucketError);
            // Fallback to database content if available
            content = template.content || '';
          }
        }

        // Parse sections from content if not already defined
        const sections =
          template.sections && template.sections.length > 0
            ? template.sections
            : parseMarkdownSections(content || "");

        console.log(
          `📋 Processing template: ${template.name} with ${sections.length} sections`
        );

        return {
          id: template.id.toString(),
          title: template.name,
          client: "Custom Template",
          date: template.created_at,
          type: template.category || "Custom",
          sections: sections,
          rawData: {
            content: content, // Use content from bucket or fallback
            sections: [],
            metadata: {
              isDefault: false,
              description: template.description || "",
              userId: template.user_id,
              updatedAt: template.updated_at,
              bucketPath: template.bucket_path, // Include bucket path for reference
              isCustomVersionOfDefault:
                template.category === "Custom" &&
                template.name.includes("Professional Business Proposal"),
            },
          },
        };
      })
    );

    return processedTemplates;
  } catch (error) {
    console.error("❌ Error fetching proposal templates:", error);
    return [];
  }
};

// Create custom template with bucket storage
export const createCustomTemplate = async (template: {
  name: string;
  description: string;
  content: string;
  sections?: string[];
  category?: string;
  userId: string;
  isCustomVersionOfDefault?: boolean;
}) => {
  try {
    // Auto-extract sections from content if not provided
    const sectionsToSave =
      template.sections || parseMarkdownSections(template.content);

    console.log("📋 Creating template with sections:", sectionsToSave);

    // Save markdown to proposaltemplates bucket
    console.log(`💾 Saving template markdown to proposaltemplates bucket...`);

    const sanitizedName = template.name.replace(/[^a-zA-Z0-9\-_\s]/g, '').replace(/\s+/g, '_');
    const timestamp = Date.now();
    const fileName = `${sanitizedName}_${timestamp}.md`;
    const bucketPath = `${template.userId}/${fileName}`;

    console.log(`📁 Bucket path: ${bucketPath}`);
    console.log(`📊 Markdown content length: ${template.content.length} characters`);

    // Upload markdown to Supabase storage
    const { error: uploadError } = await supabase.storage
      .from('proposaltemplates')
      .upload(bucketPath, template.content, {
        contentType: 'text/markdown',
        upsert: true,
      });

    if (uploadError) {
      console.error("❌ Error uploading to bucket:", uploadError);
      throw new Error(`Failed to upload markdown to bucket: ${uploadError.message}`);
    }

    console.log(`✅ Markdown uploaded successfully to bucket: ${bucketPath}`);

    // Create template record with bucket path
    const { data, error } = await supabase
      .from("scopingai_proposaltemplates")
      .insert({
        name: template.name,
        description: template.description,
        markdown_path: bucketPath, // Store the bucket path
        bucket_path: bucketPath,   // Also store in bucket_path column
        content: null, // Don't store content in database, use bucket instead
        sections: null, // Don't store sections in database, extract from markdown file
        category: template.category || "Custom",
        user_id: template.userId,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating template:", error);
      throw error;
    }

    console.log(`✅ Template record created successfully: ${data.id}`);
    return data;
  } catch (error) {
    console.error("Error creating template:", error);
    throw error;
  }
};

// Create custom version of default template
export const createCustomVersionOfDefaultTemplate = async (
  defaultTemplateId: string,
  customizations: {
    name?: string;
    description?: string;
    content?: string;
    sections?: string[];
  },
  userId: string
) => {
  try {
    // Find the default template
    const defaultTemplate = DEFAULT_TEMPLATES.find(
      (t) => t.id === defaultTemplateId
    );
    if (!defaultTemplate) {
      throw new Error("Default template not found");
    }

    // Create custom version with user modifications
    const customTemplate = {
      name: customizations.name || defaultTemplate.name,
      description: customizations.description || defaultTemplate.description,
      content: customizations.content || defaultTemplate.content,
      sections: customizations.sections || defaultTemplate.sections,
      category: "Custom",
      userId,
      isCustomVersionOfDefault: true,
    };

    const result = await createCustomTemplate(customTemplate);

    console.log(
      `✅ Created custom version of default template for user ${userId}`
    );
    return result;
  } catch (error) {
    console.error("Error creating custom version of default template:", error);
    throw error;
  }
};

// Duplicate any template (default or custom)
export const duplicateTemplate = async (
  templateId: string,
  customizations: {
    name?: string;
    description?: string;
    content?: string;
    sections?: string[];
  },
  userId: string
) => {
  try {
    // First check if it's a default template
    const defaultTemplate = DEFAULT_TEMPLATES.find((t) => t.id === templateId);

    if (defaultTemplate) {
      // Use existing function for default templates
      return await createCustomVersionOfDefaultTemplate(
        templateId,
        customizations,
        userId
      );
    }

    // For custom templates, fetch from database
    const { data: customTemplate, error } = await supabase
      .from("scopingai_proposaltemplates")
      .select("*")
      .eq("id", templateId)
      .single();

    if (error || !customTemplate) {
      throw new Error("Template not found");
    }

    // Create duplicate of custom template
    const duplicateTemplate = {
      name: customizations.name || customTemplate.name,
      description: customizations.description || customTemplate.description,
      content: customizations.content || customTemplate.content,
      sections: customizations.sections || customTemplate.sections,
      category: customTemplate.category || "Custom",
      userId,
      isCustomVersionOfDefault: false,
    };

    const result = await createCustomTemplate(duplicateTemplate);

    console.log(`✅ Duplicated template ${templateId} for user ${userId}`);
    return result;
  } catch (error) {
    console.error("Error duplicating template:", error);
    throw error;
  }
};

// Update custom template with bucket storage - Admin only
export const updateCustomTemplate = async (
  templateId: string,
  updates: {
    name?: string;
    description?: string;
    content?: string;
    sections?: string[];
    category?: string;
  },
  userId: string
) => {
  try {
    // Validate admin access
    await validateAdminAccess(userId);
    // Auto-extract sections from content if content is being updated but sections are not provided
    let sectionsToUpdate = updates.sections;
    if (updates.content && !updates.sections) {
      sectionsToUpdate = parseMarkdownSections(updates.content);
      console.log("📋 Auto-extracted sections for update:", sectionsToUpdate);
    }

    let updateData: any = {
      ...(updates.name && { name: updates.name }),
      ...(updates.description && { description: updates.description }),
      ...(updates.category && { category: updates.category }),
      // Don't store sections in database, extract from markdown file instead
      sections: null,
      updated_at: new Date().toISOString(),
    };

    // If content is being updated, save to bucket
    if (updates.content) {
      // First get the current template to get user_id
      const { data: currentTemplate, error: fetchError } = await supabase
        .from("scopingai_proposaltemplates")
        .select("user_id, name, bucket_path")
        .eq("id", templateId)
        .single();

      if (fetchError) {
        console.error("Error fetching current template:", fetchError);
        throw fetchError;
      }

      console.log(`💾 Updating template content in bucket...`);

      const templateName = updates.name || currentTemplate.name;
      const sanitizedName = templateName.replace(/[^a-zA-Z0-9\-_\s]/g, '').replace(/\s+/g, '_');
      const timestamp = Date.now();
      const fileName = `${sanitizedName}_${timestamp}.md`;
      const bucketPath = `${currentTemplate.user_id}/${fileName}`;

      console.log(`📁 New bucket path: ${bucketPath}`);
      console.log(`📊 Markdown content length: ${updates.content.length} characters`);

      // Upload new content to bucket
      const { error: uploadError } = await supabase.storage
        .from('proposaltemplates')
        .upload(bucketPath, updates.content, {
          contentType: 'text/markdown',
          upsert: true,
        });

      if (uploadError) {
        console.error("❌ Error uploading to bucket:", uploadError);
        throw new Error(`Failed to upload markdown to bucket: ${uploadError.message}`);
      }

      console.log(`✅ Markdown uploaded successfully to bucket: ${bucketPath}`);

      // Delete old file if it exists
      if (currentTemplate.bucket_path) {
        try {
          await supabase.storage
            .from('proposaltemplates')
            .remove([currentTemplate.bucket_path]);
          console.log(`🗑️ Deleted old file: ${currentTemplate.bucket_path}`);
        } catch (deleteError) {
          console.warn(`⚠️ Could not delete old file: ${currentTemplate.bucket_path}`, deleteError);
        }
      }

      // Update the bucket path in the database
      updateData.markdown_path = bucketPath;
      updateData.bucket_path = bucketPath;
      updateData.content = null; // Don't store content in database
    }

    console.log("📝 Updating template with data:", updateData);

    const { error } = await supabase
      .from("scopingai_proposaltemplates")
      .update(updateData)
      .eq("id", templateId);

    if (error) {
      console.error("Error updating template:", error);
      throw error;
    }

    console.log(`✅ Template ${templateId} updated successfully`);
  } catch (error) {
    console.error("Error updating template:", error);
    throw error;
  }
};

// Delete custom template and clean up bucket files
// Load template content from bucket for editing
export const loadTemplateContent = async (templateId: string): Promise<string> => {
  try {
    // Check if it's a default template first
    if (templateId.startsWith('default-')) {
      const defaultTemplate = DEFAULT_TEMPLATES.find(t => t.id === templateId);
      if (defaultTemplate?.content) {
        return defaultTemplate.content;
      } else {
        throw new Error(`Default template ${templateId} not found`);
      }
    }

    // For custom templates, get template record to find bucket path
    const { data: template, error: fetchError } = await supabase
      .from("scopingai_proposaltemplates")
      .select("bucket_path, markdown_path, content, name")
      .eq("id", templateId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    if (!template) {
      throw new Error("Template not found");
    }

    // Try to load from bucket first
    const bucketPath = template.bucket_path || template.markdown_path;
    if (bucketPath) {
      try {
        console.log(`📁 Downloading content from bucket: ${bucketPath}`);
        
        const { data: fileData, error: downloadError } = await supabase.storage
          .from('proposaltemplates')
          .download(bucketPath);

        if (downloadError) {
          console.error(`❌ Error downloading from bucket: ${downloadError.message}`);
          // Fallback to database content if available
          if (template.content) {
            console.log("🔄 Falling back to database content");
            return template.content;
          }
          throw downloadError;
        }

        // Convert blob to text
        const content = await fileData.text();
        console.log(`✅ Content loaded from bucket: ${content.length} characters`);
        return content;
      } catch (bucketError) {
        console.error(`❌ Error processing bucket content:`, bucketError);
        
        // Fallback to database content if available
        if (template.content) {
          console.log("🔄 Falling back to database content");
          return template.content;
        }
        throw bucketError;
      }
    }

    // If no bucket path, use database content
    if (template.content) {
      console.log("📄 Using database content");
      return template.content;
    }

    // No content found anywhere
    throw new Error("No content found for template");
  } catch (error) {
    console.error("❌ Error loading template content:", error);
    throw error;
  }
};

export const deleteCustomTemplate = async (templateId: string, userId: string) => {
  try {
    // Validate admin access
    await validateAdminAccess(userId);
    // First get the template to get the bucket path
    const { data: template, error: fetchError } = await supabase
      .from("scopingai_proposaltemplates")
      .select("bucket_path")
      .eq("id", templateId)
      .single();

    if (fetchError) {
      console.error("Error fetching template for deletion:", fetchError);
      throw fetchError;
    }

    // Delete the template record
    const { error } = await supabase
      .from("scopingai_proposaltemplates")
      .delete()
      .eq("id", templateId);

    if (error) {
      console.error("Error deleting template:", error);
      throw error;
    }

    // Delete the file from bucket if it exists
    if (template?.bucket_path) {
      try {
        const { error: deleteFileError } = await supabase.storage
          .from('proposaltemplates')
          .remove([template.bucket_path]);

        if (deleteFileError) {
          console.warn(`⚠️ Could not delete file from bucket: ${template.bucket_path}`, deleteFileError);
        } else {
          console.log(`🗑️ Deleted file from bucket: ${template.bucket_path}`);
        }
      } catch (bucketError) {
        console.warn(`⚠️ Error deleting file from bucket:`, bucketError);
      }
    }

    console.log(`✅ Template ${templateId} deleted successfully`);
  } catch (error) {
    console.error("Error deleting template:", error);
    throw error;
  }
};

// Additional functions for document library
export const makeDocumentPublic = async (documentId: string) => {
  // Implementation for making document public
  console.log("Making document public:", documentId);
};

export const saveDocumentToStorage = async (document: DocumentData) => {
  // Implementation for saving document to storage
  console.log("Saving document to storage:", document);
};

export const fetchMarkdownContent = async (path: string) => {
  // Implementation for fetching markdown content
  console.log("Fetching markdown content:", path);
  return "";
};
